import React, { useState, useEffect } from "react";
import { SetupDataPageTemplate } from "../../../components/setup-pages/SetupDataPageTemplate";
import { Field, Form, FieldError } from "react-jsonschema-form-validation";
import { setupData } from "../../../configs/routes";
import {
	fromUnixTime,
	millisecondsToSeconds,
	secondsToMilliseconds,
	getYear,
	getDaysInMonth,
	getDate,
	getMonth,
	getUnixTime,
} from "date-fns";
import _ from "lodash";
import { resetUserSettings, resetUserPid } from "../../../services/userService";
import { getMonthAbbreviation } from "../../../services/dateService";
import { pathRoot } from "../YourDetails";
import { Select } from "../../../components/common/Select";
import { sanitize } from "dompurify";
import { FormValidator } from "../../../components/common/FormValidator";
import phone from "phone";

export function Page1({ userPid, yourDetails }) {
	const sexOptions = [
		{
			label: "Male",
			value: "Male",
			id: "male",
		},
		{
			label: "Female",
			value: "Female",
			id: "female",
		},
		{
			label: "Prefer Not to Say",
			value: "PreferNotToSay",
			id: "preferNotToSay",
		},
	];

	const schema = {
		type: "object",
		properties: {
			firstName: { type: "string", maxLength: 64 },
			surname: { type: "string", maxLength: 64 },
			postalCode: { type: "string", minLength: 6, maxLength: 8 },
		},
		required: ["firstName", "surname", "sex", "postalCode", "dob"],
	};

	const formFieldsDefault = {
		firstName: userPid.firstName ?? null,
		surname: userPid.surname ?? null,
		sex: userPid.sex ?? null,
		mobilePhone: userPid.mobilePhone ?? null,
		postalCode: userPid.postalCode ?? null,
		dob: userPid.dob
			? fromUnixTime(millisecondsToSeconds(userPid.dob))
			: Date.now(),
	};

	const [formData, setFormData] = useState(formFieldsDefault);

	const setFormField = (value, propertyName) => {
		let updatedData = { ...formData };
		updatedData[propertyName] = value;
		setFormData(updatedData);
	};

	const years = _.range(1900, getYear(Date.now()));
	const yearsOptions = years.map((y) => {
		return {
			id: y,
			label: y,
			value: y,
		};
	});

	const months = _.range(12);
	const monthsOptions = months.map((m) => {
		return {
			id: m,
			label: getMonthAbbreviation(m),
			value: m,
		};
	});

	const [daysInMonth, setDaysInMonth] = useState(
		getDaysInMonth(fromUnixTime(millisecondsToSeconds(userPid.dob)))
	);

	const days = _.range(1, daysInMonth + 1);
	const dayOptions = days.map((d) => {
		return {
			id: d,
			label: d,
			value: d,
		};
	});

	const [dateOfBirth, setDateOfBirth] = useState(
		fromUnixTime(millisecondsToSeconds(userPid.dob))
	);

	const initialDate = dayOptions.find(
		(date) => date.value === getDate(dateOfBirth)
	);
	const initialMonth = monthsOptions.find(
		(month) => month.value === getMonth(dateOfBirth)
	);
	const initialYear = yearsOptions.find(
		(year) => year.value === getYear(dateOfBirth)
	);
	const [currentDateOption, setCurrentDateOption] = useState(initialDate);
	const [currentMonthOption, setCurrentMonthOption] = useState(initialMonth);
	const [currentYearOption, setCurrentYearOption] = useState(initialYear);

	useEffect(() => {
		const d = new Date(
			Date.UTC(
				currentYearOption.value,
				currentMonthOption.value,
				currentDateOption.value
			)
		);
		setDateOfBirth(d);
	}, [currentDateOption, currentMonthOption, currentYearOption]);

	useEffect(() => {
		const d = secondsToMilliseconds(getUnixTime(dateOfBirth));
		setFormField(d, "dob");
	}, [dateOfBirth]);

	useEffect(() => {
		const d = getDaysInMonth(
			new Date(Date.UTC(currentYearOption.value, currentMonthOption.value))
		);
		setDaysInMonth(d);
		if (currentDateOption.value > d) {
			setCurrentDateOption(dayOptions.find((date) => date.value === 1));
		}
	}, [currentMonthOption, currentYearOption, currentDateOption, dayOptions]);

	const onClickNext = () => {
		userPid.firstName = formData.firstName;
		userPid.surname = formData.surname;
		userPid.sex = formData.sex;
		userPid.mobilePhone = formData.mobilePhone;
		userPid.postalCode = formData.postalCode;
		userPid.dob = formData.dob;
	};

	const onClickBack = () => {
		resetUserSettings();
		resetUserPid();
	};

	const sexElements = sexOptions.map((e) => {
		const element = (
			<>
				<Field
					type="radio"
					name="sex"
					key={e.id}
					id={e.id}
					className={"hidden radioOption"}
					value={e.value}
					checked={formData.sex === e.value}
					onClick={() => setFormField(e.value, "sex")}
				/>
				<label
					htmlFor={e.id}
					className={`option ${
						formData.sex === e.value
							? `bg-black text-white font-medium`
							: "bg-gray-100"
					}`}
				>
					{e.label}
				</label>
			</>
		);

		return element;
	});

	const [validatorIsValid, setValidatorIsValid] = useState(false);

	const [phoneNumberValid, setPhoneNumberValid] = useState(true);
	const onChangePhoneNumber = (e) => {
		const value = e.target.value;
		if (value) {
			const check = phone(value);
			if (check.isValid === true) {
				setPhoneNumberValid(true);
			} else {
				const ukCheck = phone(value, { country: "GBR" });
				setPhoneNumberValid(ukCheck.isValid);
			}

			setFormField(sanitize(value), "mobilePhone");
		} else {
			setFormField("", "mobilePhone");
			setPhoneNumberValid(true);
		}
	};

	const handleValidate = (valid) => {
		setValidatorIsValid(valid);
	};

	const hideNext = () => {
		if (validatorIsValid === true) {
		} else {
			return true;
		}

		if (phoneNumberValid === true) {
		} else {
			return true;
		}

		const nullChecks = [];

		const addNullCheck = (value) => {
			nullChecks.push(value === null || value === undefined);

			if (typeof value === "string") {
				nullChecks.push(value.trim() === "");
			}
		};

		addNullCheck(formData.firstName);
		addNullCheck(formData.surname);
		addNullCheck(formData.sex);
		addNullCheck(formData.dob);
		addNullCheck(formData.postalCode);

		return nullChecks.some((e) => e);
	};

	const headerDetails = {
		title: "Your Details",
		currentPage: 1,
		pageCount: 2,
	};

	return (
		<SetupDataPageTemplate
			headerDetails={headerDetails}
			backTo={setupData}
			nextTo={`${pathRoot}/2`}
			onClickNext={onClickNext}
			onClickBack={onClickBack}
			hideNext={hideNext()}
		>
			<h2 className={"text-2xl mb-2 "}>Personal Details</h2>
			<p
				className={
					"mb-4 sm:mb-6 text-sm lg:text-base leading-tight lg:leading-6 font-thin"
				}
			>
				Please enter some basic details about yourself. We use your mobile
				number to send you texts, if you ask us to. The other information is
				used anonymously when we combine your data with other people's for
				analysis.
			</p>
			{/* <h3 className={"text-xl mb-2"}>Contact Data:</h3> */}

			<Form
				schema={schema}
				data={formData}
				errorMessages={{
					required: () => "You must fill out all required fields",
				}}
			>
				<div className={"max-w-lg mx-auto"}>
					<div className={"flex text-sm"}>
						<span className={"block pr-2 text-right w-26 my-auto"}>
							NHS/CHI No.
						</span>
						<div className="flex-grow">
							<span
								className={
									"block rounded border border-white px-2 box-border leading-7 w-full font-bold"
								}
							>
								{userPid.nhsNo}
							</span>
						</div>
					</div>

					<div className={"mb-2 flex text-sm"}>
						<span className={"block pr-2 text-right w-26 my-auto"}>
							Email Address
						</span>
						<div className="flex-grow">
							<span
								className={
									"block rounded border border-white px-2 box-border leading-7 w-full font-bold"
								}
							>
								{userPid.email}
							</span>
						</div>
					</div>

					<div className="flex gap-2 sm:gap-4">
						<div className={"formInputContainer col-span-6"}>
							<label className={"basicFormLabel"} htmlFor={"mobilePhone"}>
								Mobile No.
							</label>
							<div className="flex-grow">
								<input
									key="mobilePhone"
									type="text"
									className={"formField"}
									id={"mobilePhone"}
									name="mobilePhone"
									placeholder={"Mobile Phone"}
									value={formData.mobilePhone}
									onChange={onChangePhoneNumber}
								/>
								{phoneNumberValid ? null : (
									<div className={"Jfv_FieldError isTouched rounded-sm"}>
										Phone number is not valid
									</div>
								)}
							</div>
						</div>
						<div className={"formFieldContainer"}>
							<label className={"basicFormLabel"} htmlFor={"postalCode"}>
								Postcode
							</label>
							<div className="flex-grow">
								<Field
									type="text"
									key="postalCode"
									className={"formField"}
									id={"postalCode"}
									name="postalCode"
									placeholder={"Postcode"}
									value={formData.postalCode}
									onChange={(e) =>
										setFormField(sanitize(e.target.value), "postalCode")
									}
								/>
								<FieldError
									errorMessages={{
										required: () => "You must fill out all required fields",
									}}
									name={"postalCode"}
									className={"rounded-b-sm"}
								/>
							</div>
						</div>
					</div>

					{/* <h3 className={"text-xl my-3"}>Basic Data:</h3> */}
					<div className={"sm:flex mb-5 mt-3"}>
						<div className={`radioSlider`}>{sexElements}</div>
					</div>
					<div className="flex gap-2 sm:gap-4">
						<div className={"formInputContainer col-span-6"}>
							<label className={"basicFormLabel"} htmlFor={"firstName"}>
								First name
							</label>
							<div className="flex-grow">
								<Field
									type="text"
									key="firstName"
									className={"formField"}
									id={"firstName"}
									name="firstName"
									placeholder={"First Name(s)"}
									value={formData.firstName}
									onChange={(e) =>
										setFormField(sanitize(e.target.value), "firstName")
									}
								/>
								<FieldError
									errorMessages={{
										required: () => "You must fill out all required fields",
									}}
									name={"firstName"}
									className={"rounded-b-sm"}
								/>
							</div>
						</div>
						<div className={"formInputContainer col-span-6"}>
							<label className={"basicFormLabel"} htmlFor={"surname"}>
								Surname
							</label>
							<div className="flex-grow">
								<Field
									type="text"
									key="surname"
									className={"formField"}
									id={"surname"}
									name="surname"
									placeholder={"Surname"}
									value={formData.surname}
									onChange={(e) =>
										setFormField(sanitize(e.target.value), "surname")
									}
								/>
								<FieldError
									errorMessages={{
										required: () => "You must fill out all required fields",
									}}
									name={"surname"}
									className={"rounded-b-sm"}
								/>
							</div>
						</div>
					</div>
					<div className={"formInputContainer"}>
						<label className={"basicFormLabel"} htmlFor={"dob"}>
							Date of Birth
						</label>
						<div className="flex-grow flex sm:gap-4 gap-2">
							<div className="w-3/4">
								<Select
									options={dayOptions}
									selectedOption={currentDateOption}
									handleChange={(value) => {
										setCurrentDateOption(
											dayOptions.find((day) => day.id === value)
										);
									}}
									bg={"bg-gray-50"}
								/>
							</div>
							<div className="w-3/4">
								<Select
									options={monthsOptions}
									selectedOption={currentMonthOption}
									handleChange={(value) => {
										setCurrentMonthOption(
											monthsOptions.find((month) => month.id === value)
										);
									}}
									bg={"bg-gray-50"}
								/>
							</div>
							<div className="w-full">
								<Select
									options={yearsOptions}
									selectedOption={currentYearOption}
									handleChange={(value) => {
										setCurrentYearOption(
											yearsOptions.find((year) => year.id === value)
										);
									}}
									bg={"bg-gray-50"}
								/>
							</div>
						</div>
					</div>
				</div>
				<FormValidator validateCallback={handleValidate} />
			</Form>
		</SetupDataPageTemplate>
	);
}
